import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import './src/i18n';
import Layout from './components/Layout';
import LocaleSync from './components/LocaleSync';
import { ROUTES, type PageName } from './src/routes';

const HomePage = React.lazy(() => import('./pages/HomePage'));
const ProductsPage = React.lazy(() => import('./pages/ProductsPage'));
const ProductDetailPage = React.lazy(() => import('./pages/ProductDetailPage'));
const TechnologyPage = React.lazy(() => import('./pages/TechnologyPage'));
const InstallationsPage = React.lazy(() => import('./pages/InstallationsPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const InfraredHeatersPage = React.lazy(() => import('./pages/InfraredHeatersPage'));
const PortableHeatersPage = React.lazy(() => import('./pages/PortableHeatersPage'));
const InstallationGuidePage = React.lazy(() => import('./pages/InstallationGuidePage'));
const WarrantyPolicyPage = React.lazy(() => import('./pages/WarrantyPolicyPage'));
const FAQPage = React.lazy(() => import('./pages/FAQPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/TermsOfServicePage'));
const NewsPage = React.lazy(() => import('./pages/NewsPage'));
const NewsArticlePage = React.lazy(() => import('./pages/NewsArticlePage'));
const BusinessScenarioPage = React.lazy(() => import('./pages/BusinessScenarioPage'));
const ComparisonPage = React.lazy(() => import('./pages/ComparisonPage'));
const HospitalityGuidePage = React.lazy(() => import('./pages/HospitalityGuidePage'));
const OemOdmPage = React.lazy(() => import('./pages/OemOdmPage'));
const CompliancePage = React.lazy(() => import('./pages/CompliancePage'));
const SizingCalculatorPage = React.lazy(() => import('./pages/SizingCalculatorPage'));

const PAGE_COMPONENTS: Record<PageName, React.ElementType> = {
  HomePage,
  ProductsPage,
  ProductDetailPage,
  TechnologyPage,
  InstallationsPage,
  AboutPage,
  ContactPage,
  InfraredHeatersPage,
  PortableHeatersPage,
  InstallationGuidePage,
  WarrantyPolicyPage,
  FAQPage,
  PrivacyPolicyPage,
  TermsOfServicePage,
  NewsPage,
  NewsArticlePage,
  BusinessScenarioPage,
  ComparisonPage,
  HospitalityGuidePage,
  OemOdmPage,
  CompliancePage,
  SizingCalculatorPage,
};

const RouteFallback = () => {
  const { i18n } = useTranslation();
  const isRussian = (i18n.resolvedLanguage || i18n.language || '').split('-')[0] === 'ru';

  return (
    <div className="min-h-[60vh] bg-gray-50" aria-busy="true" role="status" aria-label={isRussian ? 'Загрузка' : 'Loading'}>
      <div className="min-h-[280px] md:min-h-[340px] bg-gradient-to-br from-gray-900 via-orange-900 to-gray-900 flex items-center justify-center">
        <span className="h-8 w-8 rounded-full border-2 border-white/30 border-t-white motion-safe:animate-spin" />
      </div>
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <Router>
        <LocaleSync />
        <Layout>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {ROUTES.map((route) =>
              route.redirectTo ? (
                <Route key={route.path} path={route.path} element={<Navigate to={route.redirectTo} replace />} />
              ) : (
                <Route
                  key={route.path}
                  path={route.path}
                  element={React.createElement(PAGE_COMPONENTS[route.page])}
                />
              )
            )}
          </Routes>
          </Suspense>
        </Layout>
      </Router>
    </HelmetProvider>
  );
}

export default App;
